import { NavigationStart, Router } from '@angular/router';
import { Directive, Input, ElementRef, Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { DynamicSQLService } from '../../../../services/dynamic-sql.service';
import { ColDef, GridApi, GridReadyEvent, ValueFormatterParams, RowStyle, RowClassParams, IRowNode } from 'ag-grid-enterprise';
import { ActivatedRoute } from '@angular/router';
import jsPDF from 'jspdf';
import { MatDialog } from '@angular/material/dialog';
import { CheckboxCellEditorComponent } from './checkbox-cell-editor/checkbox-cell-editor.component';
import { LoggingService } from 'src/app/services/logging.service';

@Component({
  selector: 'app-admin-grid',
  templateUrl: './dynamic-sql-grid.component.html',
  styleUrls: ['./dynamic-sql-grid.component.css']
})

export class DynamicSQLGridComponent implements OnInit {
  token!: string;
  @ViewChild('agGrid') agGrid!: AgGridAngular;
  @Input() inputTable!: string | undefined;
  showModal: 'none' | 'block' = 'none';
  baseColumns: any = [];
  dateStamp: string | null = null;
  error_msg = '';
  errorList: any[] = [];
  schemaFlag = false;
  rowGroupFlag = false;
  sideNav = true;
  columnDefs: ColDef[] = [];
  columnCount = 0;
  columnCurrent = 0;
  lastCol!: string;
  rowDataOriginal: any[] = [];
  rowGroupPanelShow = 'always';
  rowData: any[] = [];
  rowDataFiltered: any[] = [];
  searchText = '';
  refreshSearch = '';
  gridApi!: GridApi;
  pinnedTopRowData: any[] = [];
  pinnedBottomRowData: any[] = [];
  grandTotalRow: 'top' | 'bottom' = 'top';
  fontSize = 10;
  tableName!: string;
  lastTable!: string;
  dropdownOptionsMap: {
    [key: string]: {
      ref_value: string;
      insert_value: string;
      insert_col?: string;
    }[]
  } = {};
  editedRows: Set<number> = new Set();
  checkboxOptionsMap: { [key: string]: { ref_value: string; insert_value: any; insert_col: string }[] } = {};
  saveMode = 'add';
  // cache the names of display-only columns so we don't send them in KVPs
  private displayOnlyCols = new Set<string>();

  sideBar = {
    toolPanels: [
      {
        id: 'columns',
        labelDefault: 'Columns',
        labelKey: 'columns',
        iconKey: 'columns',
        toolPanel: 'agColumnsToolPanel',
      },
      {
        id: 'filters',
        labelDefault: 'Filters',
        labelKey: 'filters',
        iconKey: 'filter',
        toolPanel: 'agFiltersToolPanel',
      },
    ],
    defaultToolPanel: 'columns',
  };
  userID: any = sessionStorage.getItem('wOSAuthUser') || "";
  tableColumns: string[] = [];

  constructor(
    private route: ActivatedRoute,
    private dynamicSQLService: DynamicSQLService,
    private dialog: MatDialog,
    private loggingService: LoggingService,
  ) { }

  ngOnInit(): void {
    const tmp = "Angular Module initialized Init";
    window.addEventListener("hashchange", (event) => {
      console.log(
        "hash change", event
      )
    })
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.route.paramMap.subscribe(params => {
      const table = (this.inputTable == undefined) ? params.get('table') : this.inputTable;
      if (table) {
        this.loggingService.log('Table parameter found, table');
        const user: any = sessionStorage.getItem("wOSUser");
        if (user) {
          this.token = JSON.parse(user).stsTokenManager.accessToken;

          this.tableName = table;
          this.loadAdminData(table);
        } else {
          console.error('No wOSUser found in sessionStorage');
        }
      } else {
        this.loggingService.log('No table provided');
        console.error('No table parameter provided');
      }
    });
  }

  openModal(data: any = null): void {
    this.loggingService.log('Modal opened');
    this.showModal = "block";
  }

  closeModal(data: any = null): void {
    this.loggingService.log('Modal closed');
    this.showModal = 'none';
  }

  getRowStyle = (params: RowClassParams): RowStyle | undefined => {
    if (params.node.group) return undefined;
    const rowData = params.node.data;
    if (rowData?.isBlank && rowData.isDirty) {
      this.loggingService.log(`Row ${params.node.id} marked as blank and dirty.`);
      return { backgroundColor: '#bbddff' };
    }
    if (rowData?._rowChanged) {
      this.loggingService.log(`Row ${params.node.id} is edited (_rowChanged).`);
      return { backgroundColor: '#bbddff' };
    }
    if (this.editedRows.has(Number(params.node.id))) {
      this.loggingService.log(`Row ${params.node.id} is edited (editedRows).`);
      return { backgroundColor: '#bbddff' };
    }

    return undefined;
  };
  groupRowRendererParams = {
    suppressCount: false,
    innerRenderer: (params: any) => {
      const { value, node } = params;
      const aggData = node.aggData || {};
      const currencySum = aggData['Account Value'] || 0;
      return `<span>${value} (Total: $${currencySum.toLocaleString()})</span>`;
    },
  };
  searching: boolean = false;
  private searchTimeout: any;
  searchChange(term: string): void {
    clearTimeout(this.searchTimeout);
    this.searching = true;
    this.searchTimeout = setTimeout(() => {
      this.searchText = term.toLowerCase();
      this.gridApi.onFilterChanged();
      this.searching = false;
    }, 200);
  }

  isExternalFilterPresent(): boolean {
    return this.searchText.length > 0;
  }

  doesExternalFilterPass(node: any): boolean {
    const row = node.data;
    if (row.isBlank) {
      return true;
    }
    return Object.values(row).some((value) =>
      value?.toString().toLowerCase().includes(this.searchText)
    );
  }

  autoSizeAllColumns(): void {
    const columnsToAutoSize = this.gridApi.getColumns()?.filter(col => {
      const def = col.getColDef();
      return def.width == null || def.suppressSizeToFit !== true;
    }).map(col => col.getColId()) || [];
    this.gridApi.autoSizeColumns(columnsToAutoSize, false);
  }

  setColumnDefinitions(table: string): void {
    this.loggingService.log(`Setting column definitions for table: ${table}`);
    const actionCol: { [key: string]: string | number | null } = {
      display_order: 99,
      display_name: ' ',
      data_type: 'ACTIONS',
    };
    this.dynamicSQLService.getTableInfo(table, this.token).subscribe(
      (tableInfo) => {
        if (!tableInfo || tableInfo.length === 0) {
          this.loggingService.log(`No table data available for column definitions in table: ${table}`);
          return;
        }
        this.displayOnlyCols.clear();
        this.lastCol = tableInfo[tableInfo.length - 1].t_col;
        tableInfo.push(actionCol);
        this.columnCurrent = 0;
        this.columnCount = tableInfo.length;

        this.columnDefs = tableInfo.map((col) => {
          this.columnCurrent++;
          const isEditable = this.tableName === 'pp_table_info' ? true : Number(col.is_editable) === 1;
          if (col.ref_asname) this.displayOnlyCols.add(col.ref_asname);

          const baseColDef = {
            field: col.t_col,
            headerName: col.display_name?.replace(/ /g, '\n') || col.t_col?.replace(/ /g, '\n'),
            headerTooltip: col.display_name || col.t_col,
            editable: isEditable,
            sortable: true,
            filter: false,
            resizable: true,
            width: col.load_width != null ? Number(col.load_width) : undefined,
            tooltipField: col.t_col,
            suppressHeaderMenuButton: true,
            suppressSizeToFit: col.load_width != null,
            context: {
              t_col: col.t_col,
              ref_table: col.ref_table,
              ref_col: col.ref_col,
              data_type: col.data_type,
              rowGroup: col.ref_col,
              ref_join: col.ref_join,
              ref_where: col.ref_where,
              ref_asname: col.ref_asname,
              checkbox_col: col.checkbox_col,
              checkbox_table: col.checkbox_table,
            },

            onCellEditingStarted: this.onCellEditingStarted.bind(this),
            onCellEditingStopped: this.onCellEditingStopped.bind(this),
            onCellValueChanged: this.onCellValueChanged.bind(this),
            wrapHeaderText: false,
            autoHeaderHeight: false,
          };

          if (col.rowGroup === 1) return { ...baseColDef, rowGroup: true, hide: true };

          if (col.data_type === 'BOOLEAN') {
            return {
              ...baseColDef,
              cellEditor: 'agRichSelectCellEditor',
              cellEditorParams: { values: [true, false] },
              enableRowGroup: true,
              enableValue: false,
              valueFormatter: (p) => (p.value ? 'Yes' : 'No'),
              valueParser: (p) => p.newValue === 'Yes',
            };
          } else if (col.data_type === 'LISTBOX') {
            return {
              ...baseColDef,
              cellEditor: 'agRichSelectCellEditor',
              cellEditorParams: { values: [] },
            };
          } else if (col.data_type === 'STRING') {
            return {
              ...baseColDef,
              enableRowGroup: true,
              enableValue: false,
              editable: isEditable,
              ...(isEditable && { cellEditor: 'agTextCellEditor', cellEditorParams: { maxLength: 255 } }),
            };
          } else if (col.data_type === 'INT') {
            return {
              ...baseColDef,
              enableRowGroup: true,
              enableValue: true,
              valueParser: (p) => Number(p.newValue),
              valueFormatter: (p) => (p.value != null ? parseFloat(p.value).toFixed(0) : ''),
              aggFunc: (p) => (p.values || []).reduce((s, v) => s + (parseFloat(v) || 0), 0),
              ...(isEditable && { cellEditor: 'agNumberCellEditor', cellEditorParams: { min: 0, max: 10000 } }),
            };
          } else if (col.data_type === 'NOSUMINT') {
            return {
              ...baseColDef,
              cellEditor: 'agNumberCellEditor',
              cellEditorParams: { min: 0, max: 10000 },
              enableRowGroup: true,
              valueParser: (p) => Number(p.newValue),
              valueFormatter: (p) => (p.value != null ? parseFloat(p.value).toFixed(0) : ''),
              enableValue: true,
            };
          } else if (col.data_type === 'DATE') {
            return {
              ...baseColDef,
              cellEditor: 'agDateCellEditor',
              enableRowGroup: true,
              valueFormatter: (p) => {
                if (!p.value) return '';
                const d = new Date(p.value);
                return d.toISOString().split('T')[0];
              },
              filter: 'agDateColumnFilter',
            };
          } else if (col.data_type === 'CURRENCY') {
            return {
              ...baseColDef,
              cellEditor: 'agNumberCellEditor',
              enableValue: true,
              enableRowGroup: false,
              valueFormatter: (p) =>
                p.value ? parseFloat(p.value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '',
              valueParser: (p) => Math.round(parseFloat(p.newValue.replace(/[^\d.-]/g, ''))),
              aggFunc: (p) => (p.values || []).reduce((s, v) => s + (parseFloat(v) || 0), 0),
              cellStyle: { textAlign: 'right' },
            };
          } else if (col.data_type === 'CHECKBOX') {
            return {
              ...baseColDef,
              cellEditor: CheckboxCellEditorComponent,
              cellEditorParams: { values: [] },
              valueFormatter: (p) => (p.value ? p.value.split(',').join(', ') : ''),
              valueParser: (p) => (Array.isArray(p.newValue) ? p.newValue.join(',') : p.newValue),
            };
          } else if (col.data_type === 'ACTIONS') {
            return {
              ...baseColDef,
              cellRenderer: (params: any) => {
                const div = document.createElement('div');
                div.className = 'pointer';
                div.innerHTML = !params.data.isBlank ? `<i class="fas fa-fw fa-trash c_bonedk" title="Delete Row ${params.data.id}" id="del_${params.data.id}"></i>` : ``;
                div.addEventListener('click', (e: any) => {
                  const key = e.srcElement.id.split('_')[1];
                  this.deleteClick(this.tableName, key, e);
                });
                return div;
              },
              cellRendererParams: { table_name: this.tableName },
              cellEditorParams: { values: [] },
              field: `id`,
              editable: false,
              width: 55,
              cellClass: 'text-center',
              pinned: 'left',
              suppressHeaderMenuButton: true,
              suppressHeaderFilterButton: true,
              resizable: false,
            };
          }

          return { ...baseColDef, enableRowGroup: true, cellEditor: 'agTextCellEditor' };
        });
      },
      (error) => {
        this.loggingService.log(`Error fetching column definitions for table: ${table} - ${error.message}`);
        console.error(`Error fetching table info for ${table}:`, error);
      }
    );

    setTimeout(() => {
      if (this.lastTable != table) this.searchText = '';
      this.resetZoom();
      this.autoSizeAllColumns();
    });
  }


  formatError(error: any): string {
    if (typeof error === 'string') return error;
    if (error?.error?.Message) return error.error.Message;
    if (error?.message) return error.message;
    try {
      return JSON.stringify(error, null, 2);
    } catch (e) {
      return 'An unknown error occurred.';
    }
  }
  deleteClick(table: string, rowid: any, event: any) {
    try {
      this.saveMode = "delete";
      event.stopPropagation();

      $('.modal-title').html("Modifying " + this.tableName);
      $('.modal-body').html("Deleting record. Please wait.");
      this.showModal = "block";

      this.dynamicSQLService.deleteItem(table, rowid, this.token).subscribe(
        (response) => {
          console.log("✅ delete response", response);

          // Check for error-like content in the response string
          if (typeof response === 'string' && (
            response.toLowerCase().includes("the delete statement conflicted") ||
            response.toLowerCase().includes("conflict") ||
            response.toLowerCase().includes("error") ||
            response.toLowerCase().includes("constraint")
          )) {
            console.warn("⚠️ Detected error message in successful response");

            $('#bootstrapModal .modal-title').html(`
            <span class="c_red">
              <i class="fas fa-fw fa-exclamation-triangle me-2"></i>
              Error Deleting Record
            </span>`);
            $('#bootstrapModal .modal-body').html(this.formatError(response));
            this.showModal = 'block';
            return;
          }

          // If no error, continue
          this.editedRows.clear();
          setTimeout(() => {
            this.refreshSearch = this.searchText;
            this.loadAdminData(this.tableName);
            this.showModal = "none";
          }, 2000);
        },
        (error) => {
          console.error("❌ Delete error caught in error handler:", error);
          $('#bootstrapModal .modal-title').html(`
          <span class="c_red">
            <i class="fas fa-fw fa-exclamation-triangle me-2"></i>
            Error Deleting Record
          </span>`);
          $('#bootstrapModal .modal-body').html(this.formatError(error));
          this.showModal = 'block';
        }
      );
    } catch (ex) {
      console.error("❌ Exception in deleteClick():", ex);
      $('#bootstrapModal .modal-title').html(`
      <span class="c_red">
        <i class="fas fa-fw fa-exclamation-triangle me-2"></i>
        Unexpected Error
      </span>`);
      $('#bootstrapModal .modal-body').html(this.formatError(ex));
      this.showModal = 'block';
    }
  }

  saveChanges(): void {
    let encounteredError = false;
    $('.modal-title').html("Modifying " + this.tableName);
    $('.modal-body').html("Saving all changes.  Please wait.");
    this.showModal = "block";

    const dataset = this.gridApi.getRenderedNodes();
    const adds: IRowNode<any>[] = [];
    const edits: IRowNode<any>[] = [];

    console.log("rendered rows (" + dataset.length + ") ", dataset);
    console.log('Using baseCols from setColumnDefinitions:', this.baseColumns);

    // Step 1: Find New (Add) Rows
    dataset.forEach(datarow => {
      const excludeKeys = ["isBlank"];
      const mykeys = Object.keys(datarow.data).filter(key => !excludeKeys.includes(key));
      const myvals = Object.entries(datarow.data);
      if (datarow.data.isBlank && !this.isBlankRow(datarow.data)) {
        let keyidx = 0;
        for (let x = 0; x < mykeys.length; x++) {
          if (myvals[keyidx][x] !== null) {
            adds.push(datarow);
            break;
          }
          keyidx++;
        }
      }
    });

    console.log('Rows to ADD:', adds);
    console.log('Rows to EDIT', this.editedRows);

    // Step 2: Edit Changed Rows
    this.editedRows.forEach((z) => {
      const row: any = this.rowData.filter(o => o.id == z)[0];
      if (!row) {
        console.warn(`No row data found at index ${z}, skipping.`);
        return;
      }

      if (row?.isBlank === undefined) {
        const clean_row = this.cleanByRefs(row, 'edit');
        const clean_kvp = this.dataToKVP(clean_row);
        console.log("Clean KVP", clean_kvp);

        if (clean_kvp !== '') {
          console.log(`Calling updateItem for row ID ${row.id}`);
          this.dynamicSQLService.updateItem(this.userID, this.tableName, clean_row, row.id, this.token).subscribe(
            (response) => {
              console.log(`Row with ID ${row.id} updated successfully`, response);
            },
            (error) => {
              encounteredError = true;
              $('.modal-title').html("Error Updating Row in " + this.tableName);
              $('.modal-body').html([
                `Update failed for row ID ${row.id}:`,
                '<br/>',
                (error?.message || JSON.stringify(error)),
                '<br/><br/>',
                'Failed update payload:',
                '<br/>',
                `<pre>${JSON.stringify(row, null, 2)}</pre>`
              ].join(''));
              this.showModal = "block";
            }
          );
        }
      }
    });

    // Step 3: Insert New Rows
    adds.forEach((rowNode) => {
      const row = rowNode.data;
      const cleanRow = this.cleanByRefs(row, 'add');
      console.log('Inserting new row with:', cleanRow);
      const payload = { key_val_pairs: cleanRow };
      console.log('🧼 CleanRow before insert:', cleanRow);
      this.dynamicSQLService.insertItem(this.userID, this.tableName, payload, this.token).subscribe(
        (response) => {
          console.log('✅ Row inserted:', response);
        },
        (error) => {
          encounteredError = true;
          $('.modal-title').html("Error Adding Row in " + this.tableName);
          const errorMsg = this.formatError(error);
          const payloadMsg = JSON.stringify(payload, null, 2);
          $('.modal-body').html(`
          <p><strong>Insert failed:</strong></p>
          <pre>${errorMsg}</pre>
          <p><strong>Failed insert payload:</strong></p>
          <pre>${payloadMsg}</pre>
        `);
          this.showModal = "block";
        }
      );
    });

    // Step 4: Refresh the data
    $('.modal-body').html('Refreshing the screen.');
    setTimeout(() => {
      if (!encounteredError) {
        $('.modal-body').html('Refreshing the screen.');
        this.refreshSearch = this.searchText;
        this.rowData.forEach(row => delete row._rowChanged);
        this.loadAdminData(this.tableName);
      }
    }, 2000);
  }

  cleanByRefs(row: any, mode: 'add' | 'edit'): any {
    const cleaned: any = {};
    for (const key in row) {
      if (
        row.hasOwnProperty(key) &&
        key !== 'isBlank' &&
        key !== 'isDirty' &&
        key !== '_rowChanged' &&
        row[key] !== null &&
        row[key] !== ''
      ) {
        cleaned[key] = row[key];
      }
    }
    return cleaned;
  }

  refreshGrid(event: any) {
    this.autoSizeAllColumns();
  }

  sizeGrid() {
    const d = new Date();
    this.autoSizeAllColumns();
  }

  createBlankRow(): any {
    const blankRow: any = {};
    this.columnDefs.forEach((col) => {
      if (col.field) {
        blankRow[col.field] = '';
      }
    });
    blankRow.isBlank = true;
    return blankRow;
  }

  loadAdminData(table: string): void {
    this.gridApi.setGridOption('loading', true);
    this.editedRows.clear();
    this.dynamicSQLService.getTableData(table, this.token).subscribe(
      (data) => {
        if (data.length > 0) {
          this.loggingService.log('Admin data loaded');
          this.tableColumns = Object.keys(data[0]);
          this.tableColumns = this.tableColumns.filter(
            (column) =>
              !this.columnDefs.some(
                (colDef) =>
                  colDef.field === column &&
                  colDef.context?.ref_asname
              )
          );
        } else {
          console.warn('No data available to determine table columns.');
          this.tableColumns = [];
        }
        this.rowData = data.map((item: { id: any }) => ({
          ...item,
          id: item.id,
        }));
        this.rowData.push(this.createBlankRow());
        this.rowDataOriginal = this.rowData;
        console.log("Row Data Onload", this.rowData)
        this.schemaFlag = false;
        this.rowGroupFlag = false;
        this.setColumnDefinitions(table);
        this.searchText = (this.refreshSearch == '') ? '' : this.refreshSearch;
        this.gridApi.setGridOption('rowData', this.rowData);
        setTimeout(() => {
          this.autoSizeAllColumns();
          this.lastTable = table;
          this.gridApi.setGridOption('loading', false);
          this.closeModal();
          this.autoSizeAllColumns();
        }, 500)
      },
      (error) => console.error(`Error fetching data for ${table}:`, error)
    );
  }

  onCellEditingStarted(event: any): void {
    const colDef = event.colDef;
    const context = colDef.context;
    const field = colDef.field;
    const rowData = { ...event.node.data };

    // handle datetime/time normalization (unchanged)
    if (colDef.context?.data_type === 'DATETIME') {
      const value = event.newValue;
      if (typeof value === 'string') {
        const parts = value.split(' ');
        const dateParts = parts[0]?.split('-') || [];
        const timeParts = (parts[1] || '00:00:00').split(':');
        const normalizedDatetime = `${dateParts[2]}-${dateParts[1]}-${dateParts[0]} ${timeParts[0]}:${timeParts[1]}:${timeParts[2] || '00'}`;
        rowData[field] = normalizedDatetime;
      }
    }
    if (context?.data_type === 'TIME') {
      const value = rowData[field];
      if (value) {
        const timeRegex = /^\d{2}:\d{2}(:\d{2})?$/;
        if (!timeRegex.test(value)) {
          rowData[field] = '';
          return;
        }
        const parts = value.split(':');
        rowData[field] = parts.length === 2 ? `${parts[0]}:${parts[1]}:00` : value;
      }
    }

    // LISTBOX
    if (context?.data_type === 'LISTBOX' && context.ref_table && context.ref_col) {
      const dropdownKey = `${context.ref_asname || context.t_col}`;
      const fkTarget =
        context.ref_asname && context.ref_col
          ? context.ref_asname.replace(new RegExp(`_${context.ref_col}$`), '')
          : field;

      if (this.dropdownOptionsMap[dropdownKey]?.length > 0) {
        colDef.cellEditor = 'agSelectCellEditor';
        colDef.cellEditorParams = {
          values: this.dropdownOptionsMap[dropdownKey].map((o) => o.ref_value),
        };
        setTimeout(() => {
          event.api.startEditingCell({ rowIndex: event.node.rowIndex, colKey: field });
        }, 0);
        return;
      }

      this.dynamicSQLService
        .getDropdownOptionsForColumn(
          this.tableName,
          context.ref_table,
          context.ref_col,
          this.token,
          context.ref_where,
          context.ref_join
        )
        .subscribe(
          (options) => {
            // API returns [{ id, ref_data }]
            this.dropdownOptionsMap[dropdownKey] = options.map((o: any) => ({
              ref_value: o.ref_data,
              insert_value: o.id,
              insert_col: fkTarget,
            }));

            colDef.cellEditor = 'agSelectCellEditor';
            colDef.cellEditorParams = {
              values: this.dropdownOptionsMap[dropdownKey].map((o) => String(o.ref_value)),
            };

            setTimeout(() => {
              event.api.stopEditing();
              event.api.startEditingCell({ rowIndex: event.node.rowIndex, colKey: field });
            }, 0);
          },
          (error) => console.error('❌ Error fetching dropdown options:', error)
        );
    }
    // CHECKBOX
    if (context?.data_type === 'CHECKBOX' && context.ref_table && context.ref_col) {
      const checkboxKey = `${context.ref_asname || context.t_col}_checkbox`;
      const fkTarget =
        context.ref_asname && context.ref_col
          ? context.ref_asname.replace(new RegExp(`_${context.ref_col}$`), '')
          : field;

      if (this.checkboxOptionsMap[checkboxKey]?.length > 0) {
        colDef.cellEditorParams = {
          values: this.checkboxOptionsMap[checkboxKey].map((o) => o.ref_value),
        };
        return;
      }
      this.dynamicSQLService
        .getDropdownOptionsForColumn(
          this.tableName,
          context.ref_table,
          context.ref_col,
          this.token,
          context.ref_where,
          context.ref_join
        )
        .subscribe(
          (options) => {
            this.checkboxOptionsMap[checkboxKey] = options.map((o: any) => ({
              ref_value: o.ref_data,
              insert_value: o.id,
              insert_col: fkTarget,
            }));

            colDef.cellEditorParams = {
              values: this.checkboxOptionsMap[checkboxKey].map((o) => o.ref_value),
            };

            event.api.stopEditing();
            setTimeout(() => {
              event.api.startEditingCell({ rowIndex: event.node.rowIndex, colKey: field });
            }, 250);
          },
          (error) => console.error(`Error fetching checkbox options for key ${checkboxKey}:`, error)
        );
    }
    if (!context?.data_type || (context.data_type !== 'LISTBOX' && context.data_type !== 'CHECKBOX')) {
      colDef.cellEditor = 'agTextCellEditor';
    }
  }


  onCellValueChanged(event: any): void {
    const { column, data, newValue } = event;
    const rowId = data?.id;
    if (rowId !== undefined && rowId !== null) {
      this.editedRows.add(rowId);
      event.node.setSelected(true);
    }
    data._rowChanged = true;
    if (data.isBlank && !this.isBlankRow(data)) data.isDirty = true;
    const field = column.getColId();
    const cacheKey = column.getColDef().context?.ref_asname || field;
    let options = this.dropdownOptionsMap[cacheKey];
    if (!options && this.checkboxOptionsMap[cacheKey + '_checkbox']) {
      options = this.checkboxOptionsMap[cacheKey + '_checkbox'];
    }

    if (options?.length) {
      const selected = options.find((o) => o.ref_value === newValue);
      if (selected) {
        const targetCol = selected.insert_col || field;
        data[targetCol] = selected.insert_value;
        const asname = column.getColDef().context?.ref_asname;
        if (asname) data[asname] = selected.ref_value;
      }
    }

    event.api.redrawRows({ rowNodes: [event.node] });
  }

  onCellEditingStopped(event: any): void {
    const { column, data } = event;
    const field = column.getColId();
    const myrow = event.api.getRowNode(event.node.id);
    if (field && (data[field] === undefined || data[field] === null)) {
      data[field] = '';
      if (!event.data.isBlank && event.data.id != null) {
        this.editedRows.add(event.data.id);
        data._rowChanged = true;
      }
    }
    if (myrow?.data?.isBlank === undefined) {
      event.api.redrawRows({ rowNodes: [myrow] });
    }
    if (
      this.lastCol === event.column.colId &&
      !this.isBlankRow(data) &&
      (!data.id || data.id === '')
    ) {
      this.rowData.push(this.createBlankRow());
      this.gridApi.setGridOption('rowData', this.rowData);

      this.gridApi.startEditingCell({
        rowIndex: this.rowData.length - 1,
        colKey: String(this.columnDefs[0].field)
      });
    }
  }

  isBlankRow(data: any): boolean {
    return Object.entries(data)
      .filter(([key]) => key !== 'isBlank')
      .every(([_, value]) => value === null || value === undefined || value === '');
  }

  hasUnsavedChanges(): boolean {
    return this.rowData.some(row => row.isEdited);
  }

  dataToKVP(row: any): string {
    const keyValPairs: string[] = [];
    Object.keys(row).forEach((key) => {
      if (key === 'id' && (row[key] === '' || row[key] === null)) return;
      if (key === 'isBlank' || key === 'isDirty' || key === '_rowChanged') return;
      if (this.displayOnlyCols.has(key)) return;
      const value = row[key];
      if (value === undefined) return;

      let formattedValue: string | number;
      if (key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) {
        if (value) {
          const d = new Date(value);
          formattedValue = isNaN(d.getTime()) ? 'null' : `'${d.toISOString().slice(0, 19).replace('T', ' ')}'`;
        } else {
          formattedValue = 'null';
        }
      } else if (typeof value === 'string') {
        formattedValue = `'${value.replace(/'/g, "''")}'`;
      } else if (value === null) {
        formattedValue = 'null';
      } else {
        formattedValue = value;
      }
      keyValPairs.push(`${key}=${formattedValue}`);
    });

    return keyValPairs.join(',');
  }


  exportToCSV(): void {
    if (this.gridApi) {
      this.loggingService.log('CSV export started.');
      this.gridApi.exportDataAsCsv({
        fileName: 'grid-data.csv'
      });
      this.loggingService.log('CSV export completed successfully.');
    } else {
      this.loggingService.log('CSV export failed: Grid API is not initialized.');
      console.error('Grid API is not initialized');
    }
  }

  showRowGroups(): void {
    if (this.gridApi) {
      console.log('')
    } else {
      console.error('Grid API is not initialized');
    }
  }

  showSchema(): void {
    if (this.gridApi) {
      this.schemaFlag = !this.schemaFlag;
      this.gridApi.setGridOption('sideBar', this.schemaFlag);
      this.autoSizeAllColumns();
    } else {
      console.error('Grid API is not initialized');
    }
  }

  exportToPDF(): void {
    this.loggingService.log('Exporting grid data to PDF');
    const doc = new jsPDF({ orientation: 'landscape' });
    doc.setFont('times');
    function formatAmount(value: number): string {
      return `$${value.toLocaleString()}`;
    }
    const columns = this.gridApi.getAllDisplayedColumns().map((col) => ({
      headerName: col.getColDef().headerName || col.getColId(),
      field: col.getColId(),
    }));
    const rows: any[] = [];
    let totalHours = 0;
    let totalAmount = 0;
    this.gridApi.forEachNodeAfterFilter((node) => {
      totalHours += parseFloat(node.data.Hours) || 0;
      totalAmount += parseFloat(node.data.Amount) || 0;
    });
    const totalRow = columns.map((col) => {
      if (col.field === 'Task') return 'Total';
      if (col.field === 'Hours') return totalHours.toFixed(2);
      if (col.field === 'Amount') return formatAmount(Math.ceil(totalAmount));
      return '';
    });
    rows.push(totalRow);
    this.gridApi.forEachNodeAfterFilterAndSort((node) => {
      const row: any[] = [];
      columns.forEach((col) => {
        row.push(node.data[col.field] || '');
      });
      rows.push(row);
    });
    let client = 'All Clients';
    this.gridApi.forEachNodeAfterFilter((node) => {
      if (node.data.Client) {
        client = node.data.Client;
        return;
      }
    });
    const imgWidth = 140;
    const imgHeight = 40;
    const imgX = 15;
    const imgY = 15;
    const pdfBannerImg = 'assets/banner2.png';
    const titleX = imgX + imgWidth + 10;
    const titleY = imgY + (imgHeight / 2);
    const dateY = titleY + 10;
    (doc as any).autoTable({
      head: [columns.map((col) => col.headerName)],
      body: rows,
      margin: { top: 80, bottom: 20 },
      theme: 'plain',
      didDrawPage: function (data: any) {
        if (data.pageNumber === 1) {
          doc.addImage(pdfBannerImg, 'PNG', imgX, imgY, imgWidth, imgHeight);
          doc.setFontSize(12);
          doc.setFont('times', 'bold');
          doc.text(`Consulting Details: ${client}`, titleX, titleY);
          const currentDate = new Date().toLocaleDateString();
          doc.setFontSize(10);
          doc.setFont('times', 'normal');
          doc.text(`Created on: ${currentDate}`, titleX, dateY);
          data.settings.margin.top = 20;
        }
      },
      styles: {
        font: 'times',
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        lineWidth: 0,
        fontSize: 10,
        cellPadding: 1,
        rowHeight: 12,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        fontSize: 11
      },
      bodyStyles: { fillColor: [255, 255, 255] },
      alternateRowStyles: {
        fillColor: [255, 245, 225]
      },
      showHead: 'everyPage',
      rowPageBreak: 'avoid',
    });
    const pageCount = doc.internal.pages.length - 1;
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      const footerStr = 'Page ' + i + ' of ' + pageCount;
      doc.setFontSize(10);
      doc.text(footerStr, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, { align: 'center' });
    }
    doc.save(`ConsultingDetails_${client}.pdf`);
  }

  zoomIn() {
    this.fontSize += 1;
    this.applyZoom();
  }

  zoomOut() {
    if (this.fontSize > 10) {
      this.fontSize -= 1;
      this.applyZoom();
    }
  }

  resetZoom() {
    this.fontSize = 12;
    this.applyZoom();
  }

  applyZoom() {
    const gridContainer = document.querySelector('.ag-theme-alpine');
    if (gridContainer) {
      (gridContainer as HTMLElement).style.setProperty('--ag-font-size', `${this.fontSize}px`);
    }
  }
}

